import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Card, CardHeader, CardTitle, CardDescription, CardContent, Button, Input, Label, Textarea } from '@/components/ui'
import { Lock, Copy, Check, Trash2, Loader2, ThumbsUp, ThumbsDown, RefreshCw } from 'lucide-react'
import { toast } from 'sonner'
import { useVotes } from '@/hooks/useVotes'

export function AdyenV2() {
  const [card, setCard] = useState('')
  const [adyenKey, setAdyenKey] = useState('')
  const [version, setVersion] = useState('4.5.1')
  const [origin, setOrigin] = useState('')
  const [originKey, setOriginKey] = useState('')
  const [risk, setRisk] = useState('')
  const [ip, setIp] = useState('')
  const [payload, setPayload] = useState('')
  const [output, setOutput] = useState('')
  const [loading, setLoading] = useState(false)
  const [copied, setCopied] = useState(false)
  const [showReport, setShowReport] = useState(false)
  const [reportSite, setReportSite] = useState('')
  const { votes, castVote } = useVotes('adyen-v2')

  const handleEncrypt = async () => {
    if (!card.trim()) {
      toast.error('Card is required')
      return
    }
    if (!adyenKey.trim()) {
      toast.error('Adyen Key is required')
      return
    }
    if (!version.trim()) {
      toast.error('Version is required')
      return
    }
    if (!origin.trim()) {
      toast.error('Origin is required')
      return
    }
    if (!originKey.trim()) {
      toast.error('Origin Key is required')
      return
    }

    setLoading(true)
    try {
      const body: Record<string, string> = {
        card: card.trim(),
        adyenKey: adyenKey.trim(),
        version: version.trim(),
        origin: origin.trim(),
        originKey: originKey.trim(),
      }
      if (risk.trim()) body.risk = risk.trim()
      if (ip.trim()) body.ip = ip.trim()
      if (payload.trim()) body.payload = payload.trim()

      const response = await fetch('https://asianprozyy.us/encrypt/adyenv2', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })

      const data = await response.text()
      setOutput(data)
      toast.success('Encryption complete')
    } catch (error) {
      toast.error('Encryption failed')
      setOutput('Error: ' + (error instanceof Error ? error.message : 'Unknown error'))
    } finally {
      setLoading(false)
    }
  }

  const copyToClipboard = async () => {
    if (!output) return
    try {
      await navigator.clipboard.writeText(output)
      setCopied(true)
      toast.success('Copied to clipboard')
      setTimeout(() => setCopied(false), 2000)
    } catch {
      toast.error('Failed to copy')
    }
  }

  const clearAll = () => {
    setCard('')
    setAdyenKey('')
    setVersion('4.5.1')
    setOrigin('')
    setOriginKey('')
    setRisk('')
    setIp('')
    setPayload('')
    setOutput('')
    toast.success('Cleared')
  }

  const submitReport = () => {
    if (!reportSite.trim()) {
      toast.error('Please provide a site URL')
      return
    }
    castVote('needs_update')
    toast.success('Report submitted. We will check and update soon.')
    setShowReport(false)
    setReportSite('')
  }

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-orange-500/20 to-red-500/20">
              <Lock className="h-6 w-6 text-orange-500" />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight">Adyen v2</h1>
              <p className="text-muted-foreground">Adyen v2 encryption with riskData</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => castVote('working')}>
              <ThumbsUp className="h-4 w-4 mr-2 text-green-500" />
              {votes.working}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => castVote('not_working')}>
              <ThumbsDown className="h-4 w-4 mr-2 text-red-500" />
              {votes.not_working}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setShowReport(true)}>
              <RefreshCw className="h-4 w-4 mr-2 text-yellow-500" />
              {votes.needs_update}
            </Button>
          </div>
        </div>
      </div>

      {showReport && (
        <Card className="glass border-yellow-500/50">
          <CardContent className="pt-4">
            <div className="space-y-3">
              <p className="text-sm text-yellow-500">Please provide the site URL so we can check and update:</p>
              <Input
                value={reportSite}
                onChange={(e) => setReportSite(e.target.value)}
                placeholder="https://example.com"
              />
              <div className="flex gap-2">
                <Button size="sm" onClick={submitReport}>Submit</Button>
                <Button size="sm" variant="outline" onClick={() => setShowReport(false)}>Cancel</Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="glass">
          <CardHeader>
            <CardTitle>Input</CardTitle>
            <CardDescription>Enter encryption parameters</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Card <span className="text-red-500">*</span></Label>
              <Input
                value={card}
                onChange={(e) => setCard(e.target.value)}
                placeholder="4111111111111111|01|2030|111"
                className="font-mono"
              />
            </div>
            <div className="space-y-2">
              <Label>Adyen Key <span className="text-red-500">*</span></Label>
              <Textarea
                value={adyenKey}
                onChange={(e) => setAdyenKey(e.target.value)}
                placeholder="10001|D2C1C6C11FD8671FBF417F53B033321D..."
                className="font-mono min-h-[60px]"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Version <span className="text-red-500">*</span></Label>
                <Input
                  value={version}
                  onChange={(e) => setVersion(e.target.value)}
                  placeholder="4.5.1"
                  className="font-mono"
                />
              </div>
              <div className="space-y-2">
                <Label>Origin <span className="text-red-500">*</span></Label>
                <Input
                  value={origin}
                  onChange={(e) => setOrigin(e.target.value)}
                  placeholder="https://website.com"
                  className="font-mono"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Origin Key <span className="text-red-500">*</span></Label>
              <Input
                value={originKey}
                onChange={(e) => setOriginKey(e.target.value)}
                placeholder="live_UM2Y4T66SZEOXP3V4X7WJ4B7HI5FC6NK"
                className="font-mono"
              />
            </div>
            <div className="space-y-2">
              <Label>Risk Data <span className="text-muted-foreground text-xs">(optional)</span></Label>
              <Textarea
                value={risk}
                onChange={(e) => setRisk(e.target.value)}
                placeholder="eyJ2ZXJzaW9uIjoiMS4wLjAi..."
                className="font-mono min-h-[40px]"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>IP <span className="text-muted-foreground text-xs">(optional)</span></Label>
                <Input
                  value={ip}
                  onChange={(e) => setIp(e.target.value)}
                  placeholder="192.168.1.1"
                  className="font-mono"
                />
              </div>
              <div className="space-y-2">
                <Label>Payload <span className="text-muted-foreground text-xs">(optional)</span></Label>
                <Input
                  value={payload}
                  onChange={(e) => setPayload(e.target.value)}
                  placeholder="Encrypted payload"
                  className="font-mono"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleEncrypt} disabled={loading} className="flex-1">
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Encrypt
              </Button>
              <Button variant="outline" onClick={clearAll}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="glass">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Output</CardTitle>
                <CardDescription>Encrypted result</CardDescription>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={copyToClipboard}
                disabled={!output}
              >
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <Textarea
              value={output}
              readOnly
              placeholder="Encrypted output will appear here..."
              className="font-mono min-h-[300px]"
            />
          </CardContent>
        </Card>
      </div>
      <Card className="glass">
        <CardHeader>
          <CardTitle>Request an Encryptor</CardTitle>
          <CardDescription>
            Can't find the encryptor you're looking for? Request it here.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Link to="/request-encryptor">
            <Button>Request Encryptor</Button>
          </Link>
        </CardContent>
      </Card>
    </div>
  )
}
